// contexts/AuthContext.tsx
import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	ReactNode,
	useRef,
	useMemo,
	useCallback,

} from "react";
import axios from "../lib/axios"; // axios should have baseURL + withCredentials configured
import { API_BASE_URL } from '../config';

type User = {
	id: number;
	username: string;
	email: string;
	user_type?: string;
	bap_name?: string;
	roles?: string[];
	permissions?: string[];
	default_landing_page?: string | null;
	is_staff?: boolean;
	is_superuser?: boolean;
	allow_unassigned_leads?: boolean;
};

type AuthContextType = {
	user: User | null;            // null = not authenticated
	loadingUser: boolean;        // true while bootstrapping
	login: (data: { username: string; otp: string; accepts_terms_and_privacy: boolean; bap_id?: string; bap_metadata?: any; selected_restaurant_id?: number | null }) => Promise<User | null>;
	adminLogin: (data: { username: string; otp: string }) => Promise<User | null>;
	logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	// internal state: undefined = bootstrapping, null = not auth, User = auth
	const [internalUser, setInternalUser] = useState<User | null | undefined>(undefined);
	const mountedRef = useRef(true);
	const bcRef = useRef<BroadcastChannel | null>(null);


	// derived values for consumers
	const loadingUser = internalUser === undefined;
	const userForContext = internalUser === undefined ? null : internalUser;


	// bootstrap: check session (/api/check_session/) once on mount
	useEffect(() => {
		mountedRef.current = true;


		let cancelled = false;
		const checkSession = async () => {
			try {
				// use relative path so axios.baseURL is used
				const res = await axios.get(`${API_BASE_URL}/api/check_session/`);
				console.log(res);
				if (!cancelled && mountedRef.current) {
					setInternalUser(res.data || null);
				}
			} catch (err) {
				if (!cancelled && mountedRef.current) {
					setInternalUser(null);
				}
			}
		};


		checkSession();


		// BroadcastChannel for multi-tab sync (fallback to localStorage events)
		try {
			bcRef.current = new BroadcastChannel("auth");
			bcRef.current.onmessage = (ev) => {
				if (ev.data === "logout") setInternalUser(null);
				if (ev.data === "login") checkSession();
			};
		} catch {
			// BroadcastChannel might not be available: we will use storage events on login/logout
			const onStorage = (ev: StorageEvent) => {
				if (ev.key === "auth-event") {
					if (ev.newValue === "logout") setInternalUser(null);
					if (ev.newValue === "login") checkSession();
				}
			};
			window.addEventListener("storage", onStorage);
			return () => window.removeEventListener("storage", onStorage);
		}


		return () => {
			cancelled = true;
			mountedRef.current = false;
			if (bcRef.current) bcRef.current.close();
		};
	}, []);


	// login handler
	const login = useCallback(async ({ username, otp, accepts_terms_and_privacy, bap_id, bap_metadata, selected_restaurant_id }: { username: string; otp: string; accepts_terms_and_privacy: boolean; bap_id?: string; bap_metadata?: any; selected_restaurant_id?: number | null }) => {
		// Note: assume server sets auth cookie or returns user object
		// Build request payload with optional BAP information
		const requestPayload: any = { username, otp, accepts_terms_and_privacy };
		
		// Include BAP information if provided (whitespace journey)
		if (bap_id) {
			requestPayload.bap_id = bap_id;
		}
		if (bap_metadata) {
			requestPayload.bap_metadata = bap_metadata;
		}
		// Include selected restaurant_id for pre-approved multi-restaurant flow
		if (selected_restaurant_id != null) {
			requestPayload.selected_restaurant_id = selected_restaurant_id;
		}

		const res = await axios.post(`${API_BASE_URL}/api/otp_login_or_create/`, requestPayload);
		const user = res.data || null;
		if (mountedRef.current) setInternalUser(user);


		// broadcast to other tabs
		try {
			bcRef.current?.postMessage("login");
		} catch {
			localStorage.setItem("auth-event", "login");
			localStorage.removeItem("auth-event");
		}


		return user;
	}, []);

	// admin login handler
	const adminLogin = useCallback(async ({ username, otp }: { username: string; otp: string }) => {
		const res = await axios.post(`${API_BASE_URL}/api/admin/verify_otp/`, { username, otp });
		const user = res.data || null;
		if (mountedRef.current) setInternalUser(user);

		// broadcast to other tabs
		try {
			bcRef.current?.postMessage("login");
		} catch {
			localStorage.setItem("auth-event", "login");
			localStorage.removeItem("auth-event");
		}

		return user;
	}, []);

	// logout handler
	const logout = useCallback(async () => {
		try {
			await axios.post(`${API_BASE_URL}/api/logout/`);
		} catch (err) {
			console.error("Logout error:", err);
			// continue to clear local state even if logout request failed
		} finally {
			if (mountedRef.current) setInternalUser(null);
			try {
				bcRef.current?.postMessage("logout");
			} catch {
				localStorage.setItem("auth-event", "logout");
				localStorage.removeItem("auth-event");
			}
		}
	}, []);


	// stable context value
	const value = useMemo(
		() => ({
			user: userForContext,
			loadingUser,
			login,
			adminLogin,
			logout,
		}),
		[userForContext, loadingUser, login, adminLogin, logout]
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;

};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) throw new Error("useAuth must be used within AuthProvider");
	return context;

};
